import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import {
  getCalendarFeeds, createCalendarFeed, deleteCalendarFeed, calendarFeedUrl,
  type CalendarFeedScope, type CalendarFeedTokens,
} from '../api';

// Private kalender-URL'er til abonnement i Google Kalender o.l. To slags, så de kan få hver sin
// farve i Google (Google giver én farve pr. abonneret kalender):
//   blocks   — værksted, vedligehold, syn, eftersyn markeret "blokeret for udlejning"
//   bookings — alle udlejninger (fx firmakalender for medarbejdere)
// Personlige links pr. medarbejder ligger i Brugeradministration (UsersModal).
export default function CalendarFeedSection() {
  const { t } = useTranslation();
  const [tokens, setTokens] = useState<CalendarFeedTokens | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    getCalendarFeeds()
      .then(setTokens)
      .catch(e => setError(e instanceof Error ? e.message : String(e)));
  }, []);

  const wrap = (fn: () => Promise<CalendarFeedTokens>) => async () => {
    setError(null);
    try { setTokens(await fn()); }
    catch (e) { setError(e instanceof Error ? e.message : String(e)); }
  };

  return (
    <div className="px-6 py-4 space-y-4">
      <div className="space-y-1">
        <label className="text-sm font-medium text-gray-700 block">{t('settings.calendar_feed_title')}</label>
        <p className="text-xs text-gray-400">{t('settings.calendar_feed_help')}</p>
        <p className="text-xs text-gray-400">{t('settings.calendar_feed_google_steps')}</p>
      </div>
      {tokens && (['blocks', 'bookings'] as CalendarFeedScope[]).map(scope => (
        <div key={scope} className="space-y-2">
          <div className="text-sm text-gray-700 font-medium">{t(`settings.calendar_feed_${scope}`)}</div>
          <p className="text-xs text-gray-400">{t(`settings.calendar_feed_${scope}_help`)}</p>
          <FeedLinkControl
            token={tokens[scope]}
            onCreate={wrap(() => createCalendarFeed(scope))}
            onDelete={wrap(() => deleteCalendarFeed(scope))}
          />
        </div>
      ))}
      {error && <p className="text-xs text-rose-700">{error}</p>}
    </div>
  );
}

// Link med kopiér / lav nyt (to trin — det gamle holder op med at virke) / slå fra.
// Bruges både her og pr. medarbejder i Brugeradministration.
export function FeedLinkControl({ token, onCreate, onDelete }: {
  token: string | null;
  onCreate: () => Promise<void>;
  onDelete: () => Promise<void>;
}) {
  const { t } = useTranslation();
  const [busy, setBusy] = useState(false);
  const [copied, setCopied] = useState(false);
  const [confirmRenew, setConfirmRenew] = useState(false);

  const run = async (fn: () => Promise<void>) => {
    setBusy(true); setCopied(false); setConfirmRenew(false);
    try { await fn(); } finally { setBusy(false); }
  };

  const url = token ? calendarFeedUrl(token) : null;
  const copy = async () => {
    if (!url) return;
    try { await navigator.clipboard.writeText(url); setCopied(true); } catch { /* vælg teksten manuelt */ }
  };

  if (!url) {
    return (
      <button type="button" disabled={busy} onClick={() => run(onCreate)} className="btn btn-secondary">
        {t('settings.calendar_feed_enable')}
      </button>
    );
  }
  return (
    <div className="space-y-2">
      <div className="flex gap-2 items-center">
        <input className="input w-full font-mono text-xs" value={url} readOnly onFocus={e => e.target.select()} />
        <button type="button" onClick={copy} className="btn btn-secondary whitespace-nowrap">
          {t(copied ? 'settings.calendar_feed_copied' : 'settings.calendar_feed_copy')}
        </button>
      </div>
      <div className="flex gap-4 text-xs">
        <button type="button" disabled={busy}
          onClick={() => confirmRenew ? run(onCreate) : setConfirmRenew(true)}
          className={confirmRenew ? 'text-rose-700 font-medium underline' : 'text-gray-500 hover:text-gray-900 underline'}>
          {t(confirmRenew ? 'settings.calendar_feed_renew_confirm' : 'settings.calendar_feed_renew')}
        </button>
        <button type="button" disabled={busy} onClick={() => run(onDelete)}
          className="text-gray-500 hover:text-gray-900 underline">
          {t('settings.calendar_feed_disable')}
        </button>
      </div>
    </div>
  );
}
